/**
 * Birch AI select-button enhancer — Slice 6.
 *
 * One job, as of the TrustScore leaf-badge redesign: WIRE every
 * `.birch-ai-select` button rendered by Liquid (see
 * snippets/birch-ai-select-button.liquid) to the `BirchAI.selection` API.
 * Clicking toggles add/remove; `aria-pressed` and the visual state mirror
 * what's in the selection, re-synced on every `birch:selection:changed`
 * event so the floating action bar and the chat stay coherent.
 *
 * WHAT WAS REMOVED: this file used to also INJECT a "+ Select" overlay onto
 * every <product-card> on collection/search/home/wishlist pages. That "+"
 * sat in the card's top-left corner, which the TrustScore leaf badge then
 * took over (since folded into the ring's caption in
 * snippets/product-card-trust-score.liquid), and product cards are no longer
 * a selection surface.
 *
 * The selection API, the floating action bar and the chat's Compare / Ask
 * Birch actions are all deliberately left intact — only the per-card entry
 * point is gone. Any surface that still wants an in-card toggle can render
 * the Liquid snippet directly and this file will wire it up as before.
 */

// @ts-check

(function () {
  // NOTE: `sourceForPage()` stays because Liquid-rendered buttons may omit
  // `data-birch-ai-select-source`; the click handler falls back to it.
  function sourceForPage() {
    const path = window.location.pathname || '';
    // Tagging the source helps the AI know where the user came from.
    // Allowed values come from packages/shared selection types.
    if (path.includes('/wishlist')) return 'wishlist';
    return 'collection';
  }

  // ── 3. Existing wiring ────────────────────────────────────────────────────
  /** @param {Element} btn */
  function reflectState(btn) {
    const handle = btn.getAttribute('data-birch-ai-select-handle');
    if (!handle) return;
    const w = /** @type {any} */ (window);
    const has = !!(w.BirchAI?.selection?.has?.(handle));
    btn.setAttribute('aria-pressed', String(has));
    btn.classList.toggle('birch-ai-select--on', has);
  }

  function reflectAll() {
    document.querySelectorAll('.birch-ai-select').forEach(reflectState);
  }

  /** @param {Event} ev */
  function onClick(ev) {
    const target = ev.target instanceof Element ? ev.target.closest('.birch-ai-select') : null;
    if (!target) return;
    ev.preventDefault();
    ev.stopPropagation();
    const w = /** @type {any} */ (window);
    if (!w.BirchAI?.selection) return;

    const handle = target.getAttribute('data-birch-ai-select-handle');
    if (!handle) return;
    const title = target.getAttribute('data-birch-ai-select-title') || handle;
    const source = target.getAttribute('data-birch-ai-select-source') || sourceForPage();

    if (w.BirchAI.selection.has(handle)) {
      w.BirchAI.selection.remove(handle);
    } else {
      w.BirchAI.selection.add({ source, kind: 'birch', handle, title });
    }
    // reflectAll runs anyway via the change event — call it explicitly here
    // so the visual flip is immediate even if the event listener is slow.
    reflectAll();
  }

  // ── 3b. Limit message ─────────────────────────────────────────────────────
  // When the selection is already full, `BirchAI.selection.add` refuses the
  // 5th pick and fires `birch:selection:limit`. Surface a brief toast so the
  // user knows the pick didn't register (instead of silently doing nothing).
  let limitToastEl = null;
  let limitToastTimer = null;

  /** @param {number} max */
  function showLimitToast(max) {
    if (!limitToastEl) {
      limitToastEl = document.createElement('div');
      limitToastEl.className = 'birch-ai-select-limit-toast';
      limitToastEl.setAttribute('role', 'status');
      limitToastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(limitToastEl);
    }
    limitToastEl.textContent =
      'You can compare up to ' + max + ' products at a time. Deselect one first.';
    // Reflow so re-triggering restarts the CSS transition.
    void limitToastEl.offsetWidth;
    limitToastEl.classList.add('birch-ai-select-limit-toast--on');
    if (limitToastTimer) clearTimeout(limitToastTimer);
    limitToastTimer = setTimeout(() => {
      limitToastEl?.classList.remove('birch-ai-select-limit-toast--on');
    }, 3200);
  }

  function init() {
    reflectAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('click', onClick);
  window.addEventListener('birch:selection:changed', reflectAll);
  window.addEventListener('birch:selection:limit', (ev) => {
    // Single source of truth = birch-ai-selection.js MAX_ENTRIES (exposed as
    // BirchAI.selection.max and carried on the event); the literal is only a
    // last-ditch fallback if neither is available.
    const max = /** @type {any} */ (ev).detail?.max
      || /** @type {any} */ (window).BirchAI?.selection?.max
      || 10;
    showLimitToast(max);
  });

})();
